import { createMemo, onMount, Show } from "solid-js";
import { Outlet, useLocation, useRouterState } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { Library, ScanLine, Settings, UsersRound } from "lucide-solid";

import { appState } from "@/lib/app-state";
import { stripUPrefix } from "@/lib/active-account";
import { readActiveAccountSync } from "@/lib/accounts";
import { MotionOutlet } from "@/lib/motion";
import { Link, useNavigate } from "@/lib/u-router";
import { cn } from "@famidex/shared";

import { AppBackground } from "@/components/app-background";
import { OfflineBanner } from "@/components/offline-banner";
import { AppErrorBoundary } from "@/components/app-error-boundary";
import { AppearanceToggle } from "@/components/appearance-toggle";
import { Logo } from "@/components/ui/logo";
import { Avatar } from "@/components/ui/avatar";
import { AccountSwitcherHost } from "@/features/auth/account-switcher-host";
import { RankUpWatcher } from "@/features/gamification/components/rank-up-watcher";
import { useI18n } from "@/providers/i18n-provider";
import { useTheme } from "@/providers/theme-provider";
import { useUserPreferences } from "@/providers/user-preferences-context";

import { BottomNav, TAB_GROUPS } from "./bottom-nav";

/**
 * Shell FamiDex — cabecera de marca + área de contenido (Outlet) + barra inferior
 * de 4 tabs. Sustituye al shell de sidebar del template (todo-céntrico), ya
 * purgado. Es andamiaje ESTABLE que posee Wave 0: las agentes de Wave 1 montan
 * sus rutas dentro de los tabs, no editan este archivo ni la barra.
 */
export function FamidexShell() {
  const location = useLocation();
  const routerState = useRouterState();
  const theme = useTheme();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { preferences } = useUserPreferences();

  // La cuenta ACTIVA, para pintar su foto en el disparador del switcher.
  // `routerState` registra la dependencia para que se recalcule al cambiar de
  // cuenta (el switch navega).
  const activeAccount = createMemo(() => {
    void routerState().location.pathname;
    return readActiveAccountSync();
  });
  // La foto sale de la fila local (reactiva y offline); el registro de cuentas
  // es solo el valor de arranque, porque se hidrata del `get-session` y no se
  // refresca en cada escritura.
  const activeImage = () => preferences()?.image ?? null;

  // Switcher multi-cuenta dirigido por URL (`?account=switch`); el overlay lo
  // monta <AccountSwitcherHost/> abajo. El botón sólo escribe el flag.
  const openSwitcher = () => {
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, account: "switch" }),
      replace: true,
    });
  };

  onMount(() => {
    // Señal al overlay de arranque (z-[70]) de que la app está montada y lista.
    // El e2e `waitForAppBoot` espera a que ese overlay desaparezca.
    appState.markAsReady();
  });

  const isNavigating = () => routerState().status === "pending";
  // Clave de entrada por-ruta: remonta el wrapper del <Outlet> al navegar.
  // <MotionOutlet> (lib/motion) elige la variante nativa direccional según la
  // navegación (push/pop/lateral) y respeta prefers-reduced-motion globalmente.
  const sectionKey = () => stripUPrefix(location().pathname) || "/";

  return (
    <>
      {/* Fondo atmosférico como capa fija de fondo. AppBackground es
          `fixed inset-0 overflow-hidden` y RECORTA a sus hijos, así que se usa
          como puro backdrop sibling: el contenido vive en su PROPIO contenedor
          de scroll de abajo. Ver SCROLL CONTRACT en auth-scene.tsx — meter el
          shell como hijo de AppBackground recortaba todo el contenido a la
          altura del viewport SIN scroll (body tiene `overflow:hidden`), que es
          por qué el scroll no funcionaba. */}
      <AppBackground
        patternType={theme.tokens()?.patternType}
        patternOpacity={theme.tokens()?.patternOpacity}
      />

      {/* Contenedor de scroll del shell. Alto FIJO 100dvh + overflow-y-auto: el
          scroll ocurre AQUÍ dentro (el body no scrollea). `overscroll-y-contain`
          evita el encadenamiento raro y el momentum táctil en móvil. La cabecera
          `sticky` y la bottom-nav `fixed` siguen ancladas. */}
      <div
        data-testid="app-scroll-container"
        class="relative z-10 flex h-[100dvh] w-full flex-col overflow-x-hidden overflow-y-auto overscroll-y-contain text-foreground [-webkit-overflow-scrolling:touch]"
      >
        {/* ── Cabecera de marca ─────────────────────────────────────────── */}
        <header class="sticky top-0 z-30 border-b border-border/60 bg-card/90 backdrop-blur-md pt-[var(--safe-area-inset-top)]">
          <div class="mx-auto flex h-16 w-full max-w-2xl items-center justify-between px-4">
            <Link to="/" aria-label={t(msg`FamiDex, inicio`)}>
              <Logo />
            </Link>
            <nav class="flex items-center gap-1" aria-label={t(msg`Accesos`)}>
              <AppearanceToggle />
              <Link
                to="/catalog"
                aria-label={t(msg`Catálogo`)}
                data-testid="header-catalog"
                class="grid h-10 w-10 place-items-center rounded-full text-primary transition-colors hover:bg-primary/10"
              >
                <Library class="h-5 w-5" />
              </Link>
              <Link
                to="/scanner"
                aria-label={t(msg`Escáner`)}
                data-testid="header-scanner"
                class="grid h-10 w-10 place-items-center rounded-full text-primary transition-colors hover:bg-primary/10"
              >
                <ScanLine class="h-5 w-5" />
              </Link>
              <button
                type="button"
                onClick={openSwitcher}
                aria-label={t(msg`Switch account`)}
                data-testid="account-switcher-trigger"
                class="grid h-10 w-10 place-items-center rounded-full text-primary transition-colors hover:bg-primary/10"
              >
                {/* La foto de la cuenta activa, no un icono genérico: es el
                    sitio donde el usuario espera reconocerse de un vistazo, y
                    el que delata si la clave NO llegó a la fila local. La
                    cuenta invitada no tiene identidad que ilustrar (regla
                    cero), así que conserva el icono. */}
                <Show
                  when={activeAccount()?.kind === "real" ? activeAccount() : null}
                  fallback={<UsersRound class="h-5 w-5" />}
                >
                  {(account) => (
                    <Avatar
                      userId={account().userId}
                      name={account().displayName}
                      email={account().email}
                      image={activeImage() ?? account().avatar}
                      size="md"
                      testId="shell-avatar"
                    />
                  )}
                </Show>
              </button>
              <Link
                to="/profile"
                aria-label={t(msg`Ajustes`)}
                data-testid="header-settings"
                class="grid h-10 w-10 place-items-center rounded-full text-primary transition-colors hover:bg-primary/10"
              >
                <Settings class="h-5 w-5" />
              </Link>
            </nav>
          </div>
          <div
            class={cn(
              "h-0.5 w-full origin-left bg-primary transition-transform duration-300",
              isNavigating() ? "scale-x-100" : "scale-x-0",
            )}
          />
        </header>

        <OfflineBanner />

        {/* ── Contenido — deja espacio SEGURO para la barra inferior fija ──
          (7rem cubre la altura de la nav; +safe-area-inset-bottom para que el
          último ítem nunca quede tapado en móviles con home-indicator). */}
        <main class="mx-auto w-full max-w-2xl flex-1 px-4 pt-4 pb-[calc(7rem+var(--safe-area-inset-bottom))]">
          <AppErrorBoundary inline label="route">
            <MotionOutlet routeKey={sectionKey()} tabs={TAB_GROUPS}>
              <Outlet />
            </MotionOutlet>
          </AppErrorBoundary>
        </main>

        <BottomNav />

        {/* Overlay del switcher multi-cuenta (URL-driven: `?account=switch`). */}
        <AccountSwitcherHost />

        {/* Modal de subida de rango: se dispara al cruzar un umbral de XP
          (cableado cross-módulo Wave 2; el ledger lo alimenta el servidor). */}
        <RankUpWatcher />
      </div>
    </>
  );
}
